import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { getTemplateByType } from './briefTemplates';

/**
 * Dynamic Brief Fields Component
 * Renders form fields based on project type template
 */
const DynamicBriefFields = ({ 
  projectType, 
  formData, 
  onChange, 
  compact = false,
  theme 
}) => {
  const template = getTemplateByType(projectType);
  
  if (!template || !projectType) {
    return null;
  }

  const handleFieldChange = (fieldName, value) => {
    onChange({ ...formData, [fieldName]: value });
  };

  const renderField = (field) => {
    const value = formData[field.name] || (field.type === 'checkbox' ? false : '');

    switch (field.type) {
      case 'text':
        return (
          <div key={field.name} className={compact ? "col-span-1" : ""}>
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              className="mt-1"
              required={field.required}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.name} className={compact ? "col-span-1" : ""}>
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              className="mt-1"
              required={field.required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="col-span-full">
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              className="mt-1 min-h-[80px]"
              required={field.required}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.name} className={compact ? "col-span-1" : ""}>
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value}
              onValueChange={(v) => handleFieldChange(field.name, v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center gap-2">
            <Checkbox
              id={field.name}
              checked={!!value}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
            />
            <Label htmlFor={field.name} className="text-sm cursor-pointer">
              {field.label}
            </Label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {template.sections.map((section, sectionIndex) => (
        <Card key={sectionIndex} className="border border-slate-200">
          <CardHeader className="py-3 px-4 bg-slate-50">
            <CardTitle className="text-sm font-semibold text-slate-700">
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className={compact ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-4"}>
              {/* Group checkboxes together */}
              {section.fields.filter(f => f.type !== 'checkbox').map(renderField)}
              
              {/* Render checkboxes in a grid */}
              {section.fields.some(f => f.type === 'checkbox') && (
                <div className="col-span-full grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                  {section.fields.filter(f => f.type === 'checkbox').map(renderField)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DynamicBriefFields;
